import { Card } from "@yleisradio/areena-types";
import Hls from "hls.js";
import { v4 as uuidv4 } from "uuid";

import { languageMap } from "../components/settings/AudioTracks";
import { snowplowAnalyticsExists } from "../helpers/analyticsHelpers";
import { getYleIdFromCard } from "../helpers/areenaApi";
import { recommendationsUrl } from "../service/areena-recommendations";
import { PlayerState } from "../store/store";
import { AudioTrack, PlayerEmbedParams } from "../types/common";
import { OndemandAudioTrack, Subtitles } from "../types/player-api";
import {
  AbTestAnalyticsContext,
  RecommendationAnalyticsContext
} from "../types/snowplow";

// eslint-disable-next-line complexity
const getContext = (state: PlayerState, embedParams: PlayerEmbedParams) => {
  const events: {
    data: Record<string, unknown>;
    schema: string;
  }[] = [];
  if (state.mediaId) {
    events.push({
      data: {
        media_id: state.mediaId,
        predicate: "media_playing"
      },
      schema: "iglu:fi.yle.content/media/jsonschema/2-1-1"
    });
  }
  if (state.programId) {
    events.push({
      data: {
        predicate: "media_playing",
        program_id: state.programId
      },
      schema: "iglu:fi.yle.content/program/jsonschema/2-0-1"
    });
  }
  if (state.serviceId) {
    // Source audio (article to audio) does not have serviceId
    events.push({
      data: {
        predicate: "media_playing",
        service_id: state.serviceId // Yle service ID
      },
      schema: "iglu:fi.yle.content/service/jsonschema/2-0-1"
    });
  }
  if (state.isLive) {
    events.push({
      data: { dvr_window: state.media?.dvrWindowInSeconds || null }, // DVR window size in seconds, usually this is 4 hours
      schema: "iglu:fi.yle.player/live_stream/jsonschema/2-0-0"
    });
  }
  if (state.publicationId) {
    events.push({
      data: {
        predicate: "media_playing",
        publication_id: state.publicationId
      },
      schema: "iglu:fi.yle.content/publication/jsonschema/2-0-0"
    });
  }
  if (
    embedParams.analyticsContext?.player_autoplay ||
    embedParams.analyticsContext?.service_autoplay ||
    (embedParams.autoplay &&
      embedParams.analyticsContext?.player_autoplay !== false)
  ) {
    events.push({
      data: {
        is_enabled: true,
        triggered_by: !embedParams.analyticsContext?.service_autoplay
          ? "player"
          : "service"
      },
      schema: "iglu:fi.yle.player/autoplay/jsonschema/2-0-0"
    });
  }

  return events;
};

export const getStartMediaTrackingData = (
  currentTime: number,
  state: PlayerState,
  embedParams: PlayerEmbedParams
): Record<string, unknown> => ({
  context: getContext(state, embedParams),
  pings: true,
  player: {
    currentTime,
    duration: state.isLive ? null : state.durationInSeconds || null,
    ended: false,
    label: state.media?.title?.fin ?? state.media?.title?.swe ?? "No title",
    livestream: state.isLive,
    loop: embedParams.loop,
    mediaType: state.isAudio ? "audio" : "video",
    muted: state.isMuted,
    paused: state.isPaused,
    pictureInPicture: state.isPictureInPicture,
    playbackRate: state.playbackRate,
    playerType: Hls.isSupported() && !state.isAudio ? "hlsJs" : "html5",
    quality: state.selectedVideoTrack?.label,
    volume: state.isMuted ? 0 : Math.round(state.volume * 100)
  }
});

export const getAudioTrackEventData = (
  selectedAudioTrack?: AudioTrack | OndemandAudioTrack
): Record<string, unknown> => ({
  event: {
    data: {
      language_code: resolveAudioTrackLanguage(selectedAudioTrack),
      type: resolveAudioTrackType(selectedAudioTrack)
    },
    schema: "iglu:fi.yle.player/audio_track/jsonschema/2-0-1"
  }
});

export const getSubtitlesEventData = (
  selectedSubtitles?: Subtitles
): Record<string, unknown> => ({
  event: {
    data: {
      kind: resolveTextTrackKind(selectedSubtitles), // Caption kind [translation, hardofhearing]
      language_code: resolveTextTrackLanguage(selectedSubtitles),
      type:
        selectedSubtitles && selectedSubtitles.kind !== "off"
          ? "captiontrack"
          : null
    },
    schema: "iglu:fi.yle.player/caption_track/jsonschema/2-0-1"
  }
});

export const resolveTextTrackKind = (subtitles?: Subtitles): string | null => {
  switch (subtitles?.label) {
    case "käännöstekstitys":
    case "översättning":
    case "subtitles":
      return "translation";
    case "ohjelmatekstitys":
    case "programtextning":
      return "hardofhearing";
    default:
      return null;
  }
};

const resolveTextTrackLanguage = (subtitles?: Subtitles): string | null => {
  if (!subtitles || subtitles.kind === "off") {
    return null;
  }

  for (const [key, value] of Object.entries(languageMap)) {
    if (subtitles?.language === value || subtitles?.language === key) {
      return key;
    }
  }
  return "und";
};

export const findSelectedAudioTrack = (
  selectedAudioTrackId: number,
  audioTracks?: AudioTrack[],
  audioTracksFromMediaData?: OndemandAudioTrack[]
): AudioTrack | OndemandAudioTrack | undefined => {
  if (selectedAudioTrackId === -1 && audioTracksFromMediaData) {
    return audioTracksFromMediaData[0];
  }

  return audioTracks?.find(a => a.id === selectedAudioTrackId);
};

const resolveAudioDescriptionLanguage = (language: string | undefined) => {
  switch (language) {
    case "qfi":
      return "fin";
    case "qen":
      return "eng";
    case "qsv":
    case "qsw":
      return "swe";
    default:
      return "und";
  }
};

const resolveAudioTrackLanguage = (
  audioTrack?: AudioTrack | OndemandAudioTrack
): string | null => {
  if (!audioTrack) return null;

  const language = audioTrack?.language;
  if (language) {
    if (languageMap[language]) {
      return language;
    }
    for (const [key, value] of Object.entries(languageMap)) {
      if (language === value) {
        return key;
      }
    }
  }
  return resolveAudioDescriptionLanguage(language);
};

export const resolveAudioTrackType = (
  audioTrack?: AudioTrack | OndemandAudioTrack
): string | null => {
  if (!audioTrack) return null;

  const language = audioTrack?.language;
  const type = audioTrack?.type;
  if (type) {
    return type.toLowerCase();
  }
  return language &&
    language.length === 3 &&
    !languageMap[language] &&
    language.charAt(0) === "q"
    ? "audiodescription"
    : "audiotrack";
};

export const getSessionID = (): Promise<string | undefined> =>
  new Promise((resolve, reject) => {
    // we reject if yleSnowplow is not available or not ready
    if (!snowplowAnalyticsExists()) {
      reject();
    }
    return window.yleSnowplow(function () {
      try {
        const userInfo = this.sp1.getDomainUserInfo() as string[];

        if (!userInfo[6]) {
          reject();
        }

        resolve(userInfo[6]);
      } catch (e) {
        reject(e);
      }
    });
  });

const getRecommendationEngagementEventContext = (
  recommendation: Card,
  recommendationCount: number,
  recommendationIndex: number,
  state: PlayerState,
  series?: { id?: string },
  recommendationContext?: RecommendationAnalyticsContext,
  abTestContext?: AbTestAnalyticsContext
): { data: Record<string, unknown>; schema: string }[] => {
  const context: { data: Record<string, unknown>; schema: string }[] = [
    {
      data: {
        entity_id: getYleIdFromCard(recommendation),
        predicate: "engagement_view_target"
      },
      schema: "iglu:fi.yle.content/entity/jsonschema/2-0-1"
    },
    {
      data: {
        group_index: recommendationIndex,
        type: "card"
      },
      schema: "iglu:fi.yle.ui/element/jsonschema/2-1-0"
    },
    {
      data: { type: "main" },
      schema: "iglu:fi.yle.ui/wrapper/jsonschema/2-4-0"
    }
  ];

  if (state.programId) {
    context.push({
      data: {
        predicate: "engagement_view_source",
        program_id: state.programId
      },
      schema: "iglu:fi.yle.content/program/jsonschema/2-0-1"
    });

    context.push({
      data: {
        is_paginated: false,
        label: "similar",
        num_items: recommendationCount,
        source_url: recommendationsUrl(state.programId, state.contentType),
        type: "list"
      },
      schema: "iglu:fi.yle.ui/group/jsonschema/2-4-0"
    });
  }

  if (series?.id) {
    context.push({
      data: {
        predicate: "engagement_view_source",
        series_id: series.id
      },
      schema: "iglu:fi.yle.content/series/jsonschema/2-0-0"
    });
  }

  if (recommendation.image) {
    context.push({
      data: {
        image_id: recommendation.image.id,
        predicate: "engagement_view_source"
      },
      schema: "iglu:fi.yle.content/image/jsonschema/2-0-0"
    });
  }

  if (
    recommendationContext?.recommendation_id &&
    recommendationContext?.method
  ) {
    context.push({
      data: {
        recommendation_id: recommendationContext.recommendation_id,
        method: recommendationContext.method,
        predicate: "engagement_view_source"
      },
      schema: "iglu:fi.yle.content/recommendation/jsonschema/2-5-0"
    });
  }

  if (abTestContext?.name && abTestContext?.group) {
    context.push({
      data: {
        name: abTestContext.name,
        group: abTestContext.group
      },
      schema: "iglu:fi.yle.ab/test/jsonschema/2-1-0"
    });
  }

  return context;
};

export const getRecommendationViewData = (
  recommendation: Card,
  recommendationCount: number,
  recommendationIndex: number,
  state: PlayerState,
  viewUuid: string,
  series?: { id?: string },
  recommendationContext?: RecommendationAnalyticsContext,
  abTestContext?: AbTestAnalyticsContext
): Record<string, unknown> => ({
  context: getRecommendationEngagementEventContext(
    recommendation,
    recommendationCount,
    recommendationIndex,
    state,
    series,
    recommendationContext,
    abTestContext
  ),
  event: {
    data: {
      view_entity_type: "entity",
      view_id: viewUuid,
      view_mode: "thumbnail",
      view_type: "entity"
    },
    schema: "iglu:fi.yle.engagement/view/jsonschema/2-4-0"
  }
});

export const getRecommendationClickData = (
  recommendation: Card,
  recommendationCount: number,
  recommendationIndex: number,
  state: PlayerState,
  viewUuid: string,
  action: "link" | "play",
  series?: { id?: string }
): Record<string, unknown> => ({
  context: getRecommendationEngagementEventContext(
    recommendation,
    recommendationCount,
    recommendationIndex,
    state,
    series
  ),
  event: {
    data: {
      action,
      click_id: uuidv4(),
      view_id: viewUuid
    }
  }
});
